import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import Header from "../components/Header";
import LaneNav from "../components/LaneNav";
import ComparePrices from "../components/ComparePrices";
import SuggestedMatches from "../components/SuggestedMatches";
import { toast } from "sonner";
import { ArrowLeft, ArrowsClockwise, Trash, Target, ArrowDown, ArrowSquareOut, TagSimple, Gift, Warning, PencilSimple, Check, X } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine, CartesianGrid } from "recharts";
import { outboundRetailerUrl, trackBuyNowClick } from "../lib/retailerLinks";

export default function WishlistDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [w, setW] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // Inline edit state: { product_name, target_mode: "absolute"|"percent",
  // target_price, target_percent }. Held locally until the user clicks
  // Save so the network call is a single PATCH.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/wishlist/${id}`); setW(data); }
    catch { toast.error("Not found"); navigate("/dashboard/wishlist"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const refresh = async () => {
    setRefreshing(true);
    try { const { data } = await api.post(`/wishlist/${id}/refresh`); setW(data); toast.success("Refreshed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
    finally { setRefreshing(false); }
  };

  const remove = async () => {
    if (!window.confirm("Stop watching this product?")) return;
    try { await api.delete(`/wishlist/${id}`); toast.success("Removed"); navigate("/dashboard/wishlist"); }
    catch { toast.error("Delete failed"); }
  };

  const startEdit = () => {
    setDraft({
      product_name: w.product_name || "",
      target_mode: w.target_percent ? "percent" : "absolute",
      target_price: w.target_price?.toString() || "",
      target_percent: w.target_percent?.toString() || "",
    });
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setDraft(null); };

  const saveEdit = async () => {
    if (!draft) return;
    const trimmedName = draft.product_name.trim();
    if (!trimmedName) { toast.error("Product name can't be empty"); return; }
    const payload = { product_name: trimmedName };
    if (draft.target_mode === "percent") {
      const pct = parseFloat(draft.target_percent);
      if (!pct || pct < 1 || pct > 90) {
        toast.error("Percent off must be between 1 and 90");
        return;
      }
      payload.target_percent = pct;
    } else {
      const abs = parseFloat(draft.target_price);
      if (!abs || abs <= 0) {
        toast.error("Target price must be greater than zero");
        return;
      }
      payload.target_price = abs;
      payload.target_percent = 0; // clear any prior percent
    }
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/wishlist/${id}`, payload);
      setW(data);
      setEditing(false);
      setDraft(null);
      toast.success("Wishlist item updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSavingEdit(false);
    }
  };

  if (!w) return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="max-w-[1100px] mx-auto px-6 py-12" data-testid="wishlist-detail-loading">
        <div className="space-y-4">
          <div className="h-3 w-32 bg-[#e4e4e7] animate-pulse" />
          <div className="h-10 w-2/3 bg-[#e4e4e7] animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7] mt-10">
          <div className="bg-white p-6 h-28 animate-pulse" />
          <div className="bg-white p-6 h-28 animate-pulse" />
          <div className="bg-white p-6 h-28 animate-pulse" />
        </div>
      </div>
    </div>
  );

  const hit = w.current_price <= w.target_price;
  const dropFromStart = w.starting_price - w.current_price;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        <LaneNav active="wishlist" />
        <button onClick={() => navigate("/dashboard/wishlist")} className="text-sm font-semibold inline-flex items-center gap-1 text-[#52525b] hover:text-black mb-6" data-testid="wishdetail-back">
          <ArrowLeft size={16} /> Back to wishlist
        </button>

        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
          <div className="flex-1 min-w-0">
            <div className="pg-overline">{w.retailer_name} · Wishlist</div>
            {editing ? (
              <input
                type="text"
                value={draft.product_name}
                onChange={(e) => setDraft(d => ({ ...d, product_name: e.target.value }))}
                className="mt-2 w-full max-w-2xl border-2 border-[#002FA7] py-2 px-3 font-heading text-4xl font-black tracking-tighter bg-white outline-none"
                placeholder="Product name"
                data-testid="wishdetail-edit-name-input"
                autoFocus
              />
            ) : (
              <div className="flex items-center gap-3 mt-2">
                <h1 className="font-heading text-4xl font-black tracking-tighter" data-testid="wishdetail-product-name">{w.product_name}</h1>
                <button
                  onClick={startEdit}
                  className="text-[#52525b] hover:text-[#002FA7] transition-colors"
                  title="Edit name and target"
                  data-testid="wishdetail-edit-btn"
                >
                  <PencilSimple size={20} weight="bold" />
                </button>
              </div>
            )}
            {w.scraped_name && w.scraped_name !== w.product_name && (
              <div className="font-mono-pg text-[10px] uppercase tracking-widest text-[#52525b] mt-2" data-testid="wishdetail-scraped-name">
                Listed as · {w.scraped_name}
              </div>
            )}
            {!editing && w.notes && <div className="font-mono-pg text-xs text-[#52525b] mt-2">{w.notes}</div>}
          </div>
          <div className="flex flex-wrap gap-3">
            {editing ? (
              <>
                <button
                  onClick={saveEdit}
                  disabled={savingEdit}
                  className="bg-[#16A34A] text-white font-bold px-5 py-2.5 inline-flex items-center gap-2 hover:bg-[#15803D] disabled:opacity-60"
                  data-testid="wishdetail-edit-save-btn"
                >
                  <Check size={16} weight="bold" /> {savingEdit ? "Saving…" : "Save"}
                </button>
                <button
                  onClick={cancelEdit}
                  disabled={savingEdit}
                  className="border border-black font-bold px-5 py-2.5 inline-flex items-center gap-2 hover:bg-black hover:text-white"
                  data-testid="wishdetail-edit-cancel-btn"
                >
                  <X size={16} weight="bold" /> Cancel
                </button>
              </>
            ) : (
              <>
                {w.product_url && (
                  <a
                    href={outboundRetailerUrl(w.product_url, w.retailer_id)}
                    target="_blank"
                    rel="sponsored nofollow noopener noreferrer"
                    onClick={() => trackBuyNowClick({ retailerId: w.retailer_id, productUrl: w.product_url, context: "wishlist_detail" })}
                    className="bg-[#002FA7] text-white font-bold px-5 py-2.5 inline-flex items-center gap-2 hover:bg-[#00207A] transition-colors"
                    data-testid="wishlist-buy-now-btn"
                  >
                    <ArrowSquareOut size={16} weight="bold" /> Buy now at {w.retailer_name}
                  </a>
                )}
                <button onClick={refresh} disabled={refreshing} className="border border-black font-bold px-5 py-2.5 inline-flex items-center gap-2 hover:bg-black hover:text-white transition-colors disabled:opacity-60" data-testid="wishdetail-refresh-btn">
                  <ArrowsClockwise size={16} className={refreshing ? "animate-spin" : ""} /> Refresh
                </button>
                <button onClick={remove} className="border border-[#FF3B30] text-[#FF3B30] font-bold px-5 py-2.5 inline-flex items-center gap-2 hover:bg-[#FF3B30] hover:text-white" data-testid="wishdetail-delete-btn">
                  <Trash size={16} /> Stop watching
                </button>
              </>
            )}
          </div>
        </div>

        {/* Sale-in-progress callout — fires when the scraper detected a higher
            `was_price` than the current listed price. Helps users avoid
            chasing an artificially-discounted target that's about to bounce. */}
        {w.on_sale && w.was_price && (
          <div className="border border-[#D97706] bg-[#FFFBEB] p-5 mb-6 flex flex-wrap items-start gap-4" data-testid="wishlist-sale-callout">
            <div className="flex items-start gap-3 flex-1 min-w-[260px]">
              <div className="flex-shrink-0 w-10 h-10 bg-[#D97706] text-white flex items-center justify-center">
                <Warning size={20} weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="pg-overline text-[#D97706]">
                  Already on sale{w.sale_label ? ` · ${w.sale_label}` : ""}
                </div>
                <h3 className="font-heading font-bold tracking-tight mt-1 text-[#0a0a0a]">
                  Was <span className="line-through text-[#52525b]">${Number(w.was_price).toFixed(2)}</span> · now ${w.current_price.toFixed(2)}
                </h3>
                <p className="text-sm text-[#52525b] mt-1.5 leading-relaxed">
                  {w.retailer_name} appears to have this on a temporary sale.
                  {w.sale_days_remaining !== null && w.sale_days_remaining !== undefined ? (
                    <> The sale likely runs <strong>about {Math.max(0, w.sale_days_remaining)} more day{w.sale_days_remaining === 1 ? "" : "s"}</strong>{w.sale_ends_on ? <> (through {w.sale_ends_on})</> : null}.</>
                  ) : null} If you buy now and the listing returns to its usual price, your target may be harder to hit later.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Promo code panel — surfaces redeem-at-checkout codes we detect
            (e.g. PB Tech Pricebuster). Wishlist users get the same heads-up
            as tracked-purchase owners. */}
        {w.promo && w.promo.code && w.promo.promo_price && (
          <div className="border border-[#16A34A] bg-[#F0FDF4] p-5 mb-6 flex flex-wrap items-start gap-4" data-testid="wishlist-promo-panel">
            <div className="flex items-start gap-3 flex-1 min-w-[260px]">
              <div className="flex-shrink-0 w-10 h-10 bg-[#16A34A] text-white flex items-center justify-center">
                <TagSimple size={20} weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="pg-overline text-[#16A34A]">Promo code available · save ${Number(w.promo.savings || 0).toFixed(2)}</div>
                <h3 className="font-heading font-bold tracking-tight mt-1 text-[#0a0a0a]">
                  Drops to ${Number(w.promo.promo_price).toFixed(2)} with code <span className="font-mono-pg bg-[#0a0a0a] text-white px-2 py-0.5">{w.promo.code}</span>
                </h3>
                <p className="text-sm text-[#52525b] mt-1.5 leading-relaxed">
                  {w.retailer_name} is currently running{w.promo.promotion_name ? <> the "<strong>{w.promo.promotion_name}</strong>"</> : <> a promotion</>} that drops this product when the code is entered at checkout. We can't apply it for you, but it's here so you don't miss it.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Bonus panel — non-price extras (free items, store credit,
            cashback, gift cards) bundled with the product. */}
        {w.bonus && w.bonus.description && (
          <div className="border border-[#D97706] bg-[#FFFBEB] p-5 mb-6 flex flex-wrap items-start gap-4" data-testid="wishlist-bonus-panel">
            <div className="flex items-start gap-3 flex-1 min-w-[260px]">
              <div className="flex-shrink-0 w-10 h-10 bg-[#D97706] text-white flex items-center justify-center">
                <Gift size={20} weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="pg-overline text-[#D97706]">Bonus included{w.bonus.value ? ` · worth $${Number(w.bonus.value).toFixed(2)}` : ""}</div>
                <h3 className="font-heading font-bold tracking-tight mt-1 text-[#0a0a0a]">
                  {w.bonus.description}
                </h3>
                <p className="text-sm text-[#52525b] mt-1.5 leading-relaxed">
                  {w.retailer_name} is bundling this extra in alongside the product. The listed price doesn't change, so it can be easy to miss.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Price summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7] mb-10">
          <div className="bg-white p-6">
            <div className="pg-overline">Target</div>
            {editing ? (
              <div className="mt-3 space-y-3" data-testid="wishdetail-edit-target-block">
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => setDraft(d => ({ ...d, target_mode: "absolute" }))}
                    className={`flex-1 text-xs font-bold py-2 px-3 border ${draft.target_mode === "absolute" ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "bg-white text-[#0a0a0a] border-[#e4e4e7]"}`}
                    data-testid="wishdetail-target-mode-absolute"
                  >
                    $ Amount
                  </button>
                  <button
                    type="button"
                    onClick={() => setDraft(d => ({ ...d, target_mode: "percent" }))}
                    className={`flex-1 text-xs font-bold py-2 px-3 border ${draft.target_mode === "percent" ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "bg-white text-[#0a0a0a] border-[#e4e4e7]"}`}
                    data-testid="wishdetail-target-mode-percent"
                  >
                    % Off
                  </button>
                </div>
                {draft.target_mode === "absolute" ? (
                  <div className="flex items-center gap-1">
                    <span className="font-heading text-2xl font-black">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={draft.target_price}
                      onChange={(e) => setDraft(d => ({ ...d, target_price: e.target.value }))}
                      className="flex-1 border border-[#e4e4e7] py-2 px-2 text-xl font-bold outline-none focus:border-[#002FA7]"
                      placeholder="e.g. 4500"
                      data-testid="wishdetail-edit-target-price-input"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min="1"
                      max="90"
                      step="1"
                      value={draft.target_percent}
                      onChange={(e) => setDraft(d => ({ ...d, target_percent: e.target.value }))}
                      className="flex-1 border border-[#e4e4e7] py-2 px-2 text-xl font-bold outline-none focus:border-[#002FA7]"
                      placeholder="e.g. 15"
                      data-testid="wishdetail-edit-target-percent-input"
                    />
                    <span className="font-heading text-2xl font-black">%</span>
                  </div>
                )}
                {draft.target_mode === "percent" && draft.target_percent && parseFloat(draft.target_percent) > 0 && w.starting_price && (
                  <div className="font-mono-pg text-[10px] uppercase tracking-widest text-[#52525b]">
                    ≈ ${(w.starting_price * (1 - parseFloat(draft.target_percent) / 100)).toFixed(2)} off ${w.starting_price.toFixed(2)} start
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="font-heading text-3xl font-black mt-2 tracking-tighter">${w.target_price.toFixed(2)}</div>
                {w.target_percent ? (
                  <div className="pg-overline text-[#52525b] mt-1" data-testid="wishdetail-target-percent-pill">
                    {w.target_percent}% off ${w.starting_price?.toFixed(2)} start
                  </div>
                ) : null}
              </>
            )}
          </div>
          <div className="bg-white p-6">
            <div className="pg-overline">Current</div>
            <div className={`font-heading text-3xl font-black mt-2 tracking-tighter ${hit ? "text-[#16A34A]" : ""}`}>${w.current_price.toFixed(2)}</div>
          </div>
          <div className="bg-white p-6">
            <div className="pg-overline">Saved since you started</div>
            {dropFromStart > 0 ? (
              <>
                <div className="font-heading text-3xl font-black mt-2 tracking-tighter text-[#16A34A]">${dropFromStart.toFixed(2)}</div>
                {hit && <div className="pg-overline text-[#16A34A] mt-1"><Target size={10} weight="bold" /> Target hit</div>}
              </>
            ) : (
              <>
                <div className="font-heading text-3xl font-black mt-2 tracking-tighter text-[#0a0a0a]">$0</div>
                <div className="pg-overline text-[#52525b] mt-1">Waiting for a drop</div>
              </>
            )}
          </div>
        </div>

        {/* Auto-suggested cross-retailer matches (computed in the background on wishlist creation) */}
        <div className="mb-10">
          <SuggestedMatches kind="wishlist" id={w.wish_id} source={w} onLinked={load} />
        </div>

        {/* Compare across retailers */}
        <div className="mb-10">
          <ComparePrices kind="wishlist" id={w.wish_id} source={w} onChange={load} />
        </div>

        {/* Chart */}
        <div className="border border-[#e4e4e7] bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="pg-overline">Price history</div>
              <h2 className="font-heading text-2xl font-bold tracking-tight mt-1">Daily monitoring</h2>
            </div>
            {hit && (
              <span className="inline-flex items-center gap-1 bg-[#16A34A] text-white text-xs font-bold px-3 py-1.5">
                <ArrowDown size={14} weight="bold" /> Target hit
              </span>
            )}
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={w.price_history} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#f0f0f2" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#52525b", fontFamily: "IBM Plex Mono" }} />
                <YAxis tick={{ fontSize: 11, fill: "#52525b", fontFamily: "IBM Plex Mono" }} domain={["dataMin - 50", "dataMax + 50"]} />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "none", color: "#fff", fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                <ReferenceLine y={w.target_price} stroke="#16A34A" strokeDasharray="3 3" label={{ value: "Target", position: "left", fill: "#16A34A", fontSize: 10 }} />
                <Line type="monotone" dataKey="price" stroke="#002FA7" strokeWidth={2.5} dot={{ r: 4, fill: "#002FA7" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </main>
    </div>
  );
}
